<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useSenderStore } from '@/stores';
import { useQuasar, colors, setCssVar } from 'quasar';
import { PageHeader } from '@/components/clients/common';
import { InputTextbox } from '@/components/inputs';
import { DefaultImageIcon } from '.';
import Tr from '@/i18n/translation';
import { useStateStore } from '../../../stores/state.store';
import DefaultVideoIcon from './DefaultVideoIcon.vue';
import { useEventStore } from '@/stores/event/event.store';
import SlowConnectionPopUp from './SlowConnectionPopUp.vue';

const { changeAlpha } = colors;

const stateStore = useStateStore();
const eventStore = useEventStore();
const senderStore = useSenderStore();
const quasar = useQuasar();

const mainColor = ref(eventStore.getColors.mainColor);
const buttonColor = ref(eventStore.getColors.buttonColor);
const backgroundColor = ref(eventStore.getColors.backgroundColor);

const checkboxColors = {
  '--p-checkbox-background': backgroundColor.value,
  '--p-checkbox-border-color': mainColor.value,
  '--p-checkbox-checked-background': mainColor.value,
  '--p-checkbox-checked-border-color': mainColor.value,
  '--p-checkbox-checked-hover-background': mainColor.value,
  '--p-checkbox-checked-hover-border-color': backgroundColor.value,
  '--p-checkbox-hover-border-color': mainColor.value,
  '--p-checkbox-icon-checked-color': backgroundColor.value,
  '--p-checkbox-icon-checked-hover-color': backgroundColor.value,
};

const wish = ref('');
const isFiles = ref(false);
const isFileWish = ref(false);

const filesRef = ref(null);
const wishRef = ref(null);
const filesUrl = ref([]);
const fileRef = ref(null);

setCssVar('primary', mainColor.value);
setCssVar('secondary', backgroundColor.value);

const sending = computed(() => {
  return senderStore.isSending;
});

const sendedMB = computed(() => {
  const val = senderStore.sendedSize / 1048576;
  const roundedVal = Math.round((val + Number.EPSILON) * 100) / 100;
  return Math.max(roundedVal, 0.01);
});

const totalMB = computed(() => {
  const val = senderStore.totalSizeToSend / 1048576;
  return Math.round((val + Number.EPSILON) * 100) / 100;
});

const btnText = computed(() => {
  if (sending.value) {
    return Tr.t('appCore.apps.event.wedding.sendingText');
  }

  return Tr.t('appCore.apps.event.wedding.sendText');
});

const isBtnDisabled = computed(() => {
  return !isFiles.value && (!isFileWish.value || wish.value === '');
});

const btnColor = computed(() => {
  return buttonColor.value ? buttonColor.value : mainColor.value;
});

const btnColorRunning = computed(() => {
  const color = btnColor.value;
  return changeAlpha(color, -1);
});

const backgroundColorRunning = computed(() => {
  const color = backgroundColor.value;
  return changeAlpha(color, 0.5);
});

function focusToWish() {
  if (isFileWish.value && wish.value === '') {
    if (wishRef.value) {
      wishRef.value.$el.scrollIntoView();
    }
    quasar.notify({
      message: Tr.t('appCore.apps.event.sender.infos.emptyWish'),
      color: 'primary',
      textColor: 'secondary',
    });
  }
}

function handleEndSending() {
  if (senderStore.files.length === 0) {
    let message = Tr.t('appCore.apps.event.sender.infos.sendedFiles');
    message = `${message} ${Tr.t('appCore.apps.event.sender.infos.sendedProcessing')}`;

    quasar.notify({
      message: message,
      classes: 'send-success',
      position: 'center',
      color: 'primary',
      textColor: 'secondary',
    });
  } else {
    for (const file of senderStore.files) {
      if (supportedImage(file.file)) {
        filesUrl.value.push({
          isSupported: true,
          name: file.file.name,
          url: URL.createObjectURL(file.file),
        });
      } else {
        filesUrl.value.push({
          isSupported: false,
          name: file.file.name,
          url: URL.createObjectURL(file.file),
        });
      }
      wish.value = file.tags[0]?.wish ?? '';
    }

    if (wish.value !== '') {
      isFileWish.value = true;
    } else {
      isFiles.value = true;
    }

    const message =
      Tr.t('appCore.apps.event.sender.infos.sendingProblem.sended') +
      ' ' +
      senderStore.lastHistoryElement.sendedFiles +
      ' ' +
      Tr.t('appCore.apps.event.sender.infos.sendingProblem.from') +
      ' ' +
      senderStore.lastHistoryElement.filesToSend +
      ' ' +
      Tr.t('appCore.apps.event.sender.infos.sendingProblem.files') +
      '. ' +
      Tr.t('appCore.apps.event.sender.infos.sendingProblem.checkFiles');
    quasar.notify({
      message: message,
      classes: 'send-success',
      position: 'center',
      color: 'primary',
      textColor: 'secondary',
    });
  }

  senderStore.sendedInfoShowed = true;
}

watch(
  () => senderStore.filesToSend,
  () => {
    if (senderStore.filesToSend === 0) {
      handleEndSending();
    }
  },
);

async function sendPicture() {
  if (isFiles.value) {
    let res;
    if (stateStore.getCommonData.isNewSender) {
      res = await senderStore.sendImages('createSenderStream', 'sendByStream');
    } else {
      res = await senderStore.sendImages(
        'createSenderStream',
        'sendGalleryImages',
      );
    }

    if (!res) {
      quasar.notify({
        type: 'negative',
        message: Tr.t('appCore.apps.event.sender.errors.default'),
      });
    }
  } else if (isFileWish.value) {
    senderStore.addTags(filesUrl.value[0].name, [{ wish: wish.value }]);

    let res;
    if (stateStore.getCommonData.isNewSender) {
      res = await senderStore.sendImages('createSenderStream', 'sendByStream');
    } else {
      res = await senderStore.sendImages(
        'createSenderStream',
        'sendGalleryImages',
      );
    }

    if (!res) {
      quasar.notify({
        type: 'negative',
        message: Tr.t('appCore.apps.event.sender.errors.default'),
      });
    }
  } else {
    quasar.notify({
      type: 'negative',
      message: Tr.t('appCore.apps.event.sender.errors.notFiles'),
    });
  }

  for (const item of filesUrl.value) {
    URL.revokeObjectURL(item.url);
  }
  filesUrl.value = [];
  wish.value = '';
  senderStore.sendedInfoShowed = false;
  isFiles.value = false;
  isFileWish.value = false;
  isRetryDisable.value = false;
}

function onRejected() {
  quasar.notify({
    type: 'negative',
    message: Tr.t('appCore.apps.event.sender.errors.wrongFiles'),
  });
}

function onRejectedWithWish() {
  quasar.notify({
    type: 'negative',
    message: Tr.t('appCore.apps.event.sender.errors.wrongFilesWithWish'),
  });
}

function deleteImg(name) {
  senderStore.removeImage(name);
  filesUrl.value = filesUrl.value.filter((item) => {
    if (item.name === name) {
      URL.revokeObjectURL(item.url);
    }
    return item.name !== name;
  });

  if (filesUrl.value.length === 0) {
    isFiles.value = false;
    isFileWish.value = false;
  }
}

const isHidden = ref(false);

watch(isHidden, () => {
  if (!isBtnDisabled.value) {
    for (const file of filesUrl.value) {
      senderStore.updatePurpose(file?.name ?? '', choosePurpose(file));
    }
  }
});

const choosePurpose = (file) => {
  let purpose = supportedVideo(file) ? 'video-gallery' : 'photo-gallery';
  if (isHidden.value) {
    purpose = 'hidden-gallery';
  }

  return purpose;
};

function supportedImage(file) {
  return /^image\//.test(file.type) && !/.heic$/.test(file.name);
}

function supportedVideo(file) {
  return /^video\//.test(file.type);
}

function supportedFile(file) {
  return (
    /.heic$/.test(file.name) || supportedImage(file) || supportedVideo(file)
  );
}

async function previewFiles() {
  for (const file of filesRef.value.files) {
    const maxSize = 1073741824;
    if (
      supportedFile(file) &&
      filesUrl.value.length < 20 &&
      senderStore.size + file.size < maxSize
    ) {
      senderStore.addImage(file, choosePurpose(file), []);
      isFiles.value = true;
      if (supportedImage(file)) {
        filesUrl.value.push({
          isSupported: true,
          name: file.name,
          url: URL.createObjectURL(file),
        });
      } else {
        filesUrl.value.push({
          isSupported: false,
          name: file.name,
          url: URL.createObjectURL(file),
        });
      }
    } else {
      onRejected();
    }
  }
}

async function previewFile() {
  for (const file of fileRef.value.files) {
    const maxSize = 1073741824;
    if (
      supportedFile(file) &&
      filesUrl.value.length < 1 &&
      senderStore.size + file.size < maxSize
    ) {
      senderStore.addImage(file, choosePurpose(file), [{ wish: wish.value }]);
      isFileWish.value = true;
      if (supportedImage(file)) {
        filesUrl.value.push({
          isSupported: true,
          name: file.name,
          url: URL.createObjectURL(file),
        });
      } else {
        filesUrl.value.push({
          isSupported: false,
          name: file.name,
          url: URL.createObjectURL(file),
        });
      }
    } else {
      onRejectedWithWish();
    }
  }
}

const infoShowed = ref(-1);
const isChanged = ref(false);

async function showInfo(id) {
  infoShowed.value = id;
  isChanged.value = true;
}

function handleFocusOutInfo() {
  if (isChanged.value) {
    isChanged.value = false;
    return;
  }

  infoShowed.value = -1;
}

const isRetryDisable = ref(false);

function disableRetry() {
  isRetryDisable.value = true;
}

function handleWindowFocus() {
  if (
    stateStore.getCommonData.isNewSender &&
    !isRetryDisable.value &&
    senderStore.isFailedFile &&
    !senderStore.isSending &&
    senderStore.files.length !== 0
  ) {
    senderStore.retrySendImages('createSenderStream', 'sendByStream');

    for (const item of filesUrl.value) {
      URL.revokeObjectURL(item.url);
    }
    filesUrl.value = [];
    wish.value = '';
    senderStore.sendedInfoShowed = false;
    isFiles.value = false;
    isFileWish.value = false;
  }
}

onMounted(async () => {
  if (!senderStore.isSending) {
    if (!senderStore.sendedInfoShowed) {
      handleEndSending();
    } else if (senderStore.files.length !== 0) {
      for (const file of senderStore.files) {
        if (supportedImage(file.file)) {
          filesUrl.value.push({
            isSupported: true,
            name: file.file.name,
            url: URL.createObjectURL(file.file),
          });
        } else {
          filesUrl.value.push({
            isSupported: false,
            name: file.file.name,
            url: URL.createObjectURL(file.file),
          });
        }
        wish.value = file.tags[0]?.wish ?? '';
      }

      await nextTick();
      if (wish.value !== '') {
        isFileWish.value = true;
      } else {
        isFiles.value = true;
      }
    }
  }

  window.addEventListener('focus', handleWindowFocus);
});

onUnmounted(() => {
  window.removeEventListener('focus', handleWindowFocus);
});
</script>

<template>
  <div class="content">
    <PageHeader
      :main-color="mainColor"
      :background-color="backgroundColor"
      :header-text="$t('appCore.apps.event.sender.button')"
    />
    <div class="sender">
      <div v-if="!isFileWish" class="files">
        <div class="pictures">
          <div
            v-for="(fileUrl, imgId) in filesUrl"
            :key="imgId"
            class="img-preview"
          >
            <img v-if="fileUrl.isSupported" :src="fileUrl.url" alt="" />
            <DefaultImageIcon
              v-else-if="/.heic$/.test(fileUrl.name)"
              :color="mainColor"
            />
            <DefaultVideoIcon v-else :color="mainColor" />
            <div class="delete" @click="deleteImg(fileUrl.name)">
              <span class="plus">+</span>
            </div>
            <div
              v-if="/.heic$/.test(fileUrl.name)"
              v-click-outside="handleFocusOutInfo"
              class="info"
              @click="showInfo(imgId)"
            >
              <span class="plus questionmark">?</span>
              <transition name="fade" mode="out-in">
                <div v-show="imgId === infoShowed" class="info-content">
                  <p class="info-text">
                    {{ $t('appCore.apps.event.sender.infos.fileUnsupported') }}
                  </p>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <div v-if="filesUrl.length !== 0" class="file-input-wrapper">
          <input
            id="file-input"
            ref="filesRef"
            type="file"
            name="file-input"
            class="file-input"
            accept=".jpg, image/*, video/*, .heic"
            multiple
            :disabled="sending"
            @click="disableRetry"
            @change="previewFiles"
          />
          <label
            for="file-input"
            class="file-input-label"
            :class="{ disabled: sending }"
          >
            <span class="plus">+</span>
          </label>
        </div>

        <div v-else class="file-input-wrapper file-input-wrapper-empty">
          <input
            id="file-input-empty"
            ref="filesRef"
            type="file"
            name="file-input"
            class="file-input"
            accept=".jpg, image/*, video/*, .heic"
            multiple
            :disabled="sending"
            @change="previewFiles"
          />
          <label
            for="file-input-empty"
            class="file-input-label"
            :class="{ disabled: sending }"
          >
            <span class="plus">+</span>
            <span class="text">{{
              $t('appCore.apps.components.sender.sendLabel')
            }}</span>
          </label>
        </div>
      </div>
      <p v-if="!isFiles && !isFileWish" class="or">
        {{ $t('appCore.apps.event.sender.or') }}
      </p>
      <div v-if="!isFiles" class="files">
        <div v-if="filesUrl.length !== 0" class="picture">
          <div class="img-preview img-wish">
            <img
              v-if="filesUrl[0].isSupported"
              :src="filesUrl[0].url"
              alt=""
              @load="focusToWish"
            />
            <DefaultImageIcon
              v-else-if="/.heic$/.test(filesUrl[0].name)"
              :color="mainColor"
            />
            <DefaultVideoIcon v-else :color="mainColor" />
            <div class="delete" @click="deleteImg(filesUrl[0].name)">
              <span class="plus">+</span>
            </div>
          </div>
        </div>
        <div
          v-if="filesUrl.length === 0"
          class="file-input-wrapper file-input-wrapper-empty"
        >
          <input
            id="file-input-wish"
            ref="fileRef"
            type="file"
            name="file-input"
            class="file-input"
            accept=".jpg, image/*, video/*, .heic"
            :disabled="sending"
            @change="previewFile"
          />
          <label
            for="file-input-wish"
            class="file-input-label"
            :class="{ disabled: sending }"
          >
            <span class="plus">+</span>
            <span class="text">{{
              $t('appCore.apps.components.sender.sendWithWishLabel')
            }}</span>
          </label>
        </div>
        <InputTextbox
          ref="wishRef"
          v-model="wish"
          class="wish-textbox"
          :placeholder="$t('appCore.apps.event.sender.yourWishes')"
          :main-color="mainColor"
          :background-color="backgroundColor"
          text-font="Raleway"
          border-radius="10px"
          counter
          :max-length="350"
          :disabled="sending"
        />
      </div>
    </div>
    <div class="send">
      <button
        class="button btn-bold"
        :class="{ 'btn-active': sending, 'btn-disabled': isBtnDisabled }"
        :disabled="sending || isBtnDisabled"
        @click="sendPicture"
      >
        <b>{{ btnText }}</b>
        <b v-if="sending && sendedMB !== totalMB"
          >{{ sendedMB }} MB
          {{ $t('appCore.apps.components.sender.from') }}
          {{ totalMB }} MB</b
        >
        <b v-else-if="sending && sendedMB === totalMB">{{
          $t('appCore.apps.components.sender.processing')
        }}</b>
      </button>
      <div class="input-checkbox">
        <Checkbox
          v-model="isHidden"
          class="input-checkbox-hide-file"
          :style="checkboxColors"
          input-id="isHidden"
          :binary="true"
        />
        <label class="input-checkbox-label" for="isHidden">
          {{ $t('appCore.apps.components.sender.notToGallery') }}
        </label>
      </div>
    </div>
    <SlowConnectionPopUp
      :main-color="mainColor"
      :background-color="backgroundColor"
    />
  </div>
</template>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
.q-field__input,
.q-file__filler,
.q-field__label,
.q-field--outlined,
.q-field__control,
.q-field__control::after {
  color: v-bind(mainColor);
}

.send-success {
  font-size: 1.25em;
  text-align: center;
}
/* stylelint-enable selector-class-pattern */
</style>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.content {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: v-bind(backgroundColor);
}

.sender {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 80%;
  height: auto;
  margin: 0 auto;
  align-items: stretch;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 2px;
  padding-top: 185px;
  padding-bottom: 135px;
  color: v-bind(mainColor);

  @media screen and (max-width: $media-phone) {
    padding-top: 155px;
  }
}

.send {
  position: fixed;
  width: 100%;
  background-color: v-bind(backgroundColor);
  z-index: 10;
  bottom: 0;
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 5px 0 25px;

  @media screen and (max-width: $media-phone) {
    padding: 5px 0 15px;
    gap: 10px;
  }
}

.button {
  border-radius: 48.707px;
  border: 2px solid v-bind(btnColor);
  color: v-bind(backgroundColor);
  background-color: v-bind(btnColor);
  padding: 15px 25px;
  font-size: 25px;
  white-space: nowrap;
  text-align: center;
  transition: background-color 1s ease;

  &:hover {
    background-color: v-bind(backgroundColor);
    color: v-bind(btnColor);
    cursor: pointer;
  }

  @media screen and (max-width: $media-phone) {
    font-size: 18px;
    line-height: 30px;
    padding: 10px 20px;
  }
}

.btn-disabled {
  opacity: 0.5;
}

.btn-active {
  background-color: v-bind(btnColor);
  color: v-bind(backgroundColor);
  position: relative;
  overflow: hidden;
}

.btn-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    v-bind(btnColorRunning),
    v-bind(backgroundColorRunning)
  );
  border-radius: 0 26px 26px 0;
  animation: fill-loader 2s linear infinite;
}

@keyframes fill-loader {
  0% {
    left: -100%;
  }

  100% {
    left: 100%;
  }
}

.btn-bold {
  border: 3px solid v-bind(btnColor);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.or {
  text-align: center;
  font-size: 1.5em;
}

.wish-textbox {
  width: 100%;
  min-height: 150px;
  text-transform: none;

  @media screen and (max-width: $media-phone) {
    min-height: 80px;
  }
}

.pictures {
  display: flex;
  flex-flow: row wrap;
  gap: 15px;
  justify-content: left;
  align-items: stretch;

  @media screen and (max-width: $media-phone) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
}

.picture {
  display: flex;
  flex-flow: row wrap;
}

.img-preview {
  position: relative;
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: $media-phone) {
    height: 125px;
    width: 100%;
  }

  img {
    object-fit: cover;
  }

  video {
    width: 150px;
    height: 150px;

    @media screen and (max-width: $media-phone) {
      height: 125px;
      width: 100%;
    }
  }
}

.img-wish {
  width: 100%;
  height: 100%;
}

.files {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.file-input-wrapper {
  position: relative;
  display: inline-block;
  width: 150px;
  height: 150px;

  @media screen and (max-width: $media-phone) {
    width: 125px;
    height: 125px;
  }
}

.file-input-wrapper-empty {
  width: 100%;
  height: auto;
}

.file-input {
  display: none;
}

.file-input-label {
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 15px;
  justify-content: center;
  align-items: center;
  border-radius: 5.476px;
  text-align: center;
  border: 1px solid v-bind(btnColor);
  padding: 15px 40px;

  .text {
    font-size: 1.5em;

    @media screen and (max-width: $media-phone) {
      font-weight: 700;
      font-size: 1em;
    }
  }
}

.plus {
  text-align: center;
  font-size: 3em;
  line-height: 20px;
  border-radius: 50%;
  padding: 5px;
  padding-left: 7px;
  border: 2px solid v-bind(btnColor);

  @media screen and (max-width: $media-phone) {
    font-size: 2.5em;
    line-height: 18px;
  }
}

.delete {
  position: absolute;
  top: -10px;
  right: -10px;

  .plus {
    cursor: pointer;
    background-color: v-bind(backgroundColor);
    display: flex;
    transform: rotate(45deg);
    font-size: 2em;
    line-height: 15px;
    padding: 5px;
    padding-left: 7px;

    @media screen and (max-width: $media-phone) {
      font-size: 2.5em;
      line-height: 17px;
      padding-left: 6px;
    }
  }
}

.info {
  position: absolute;
  top: -10px;
  left: 0;

  .questionmark {
    cursor: pointer;
    background-color: v-bind(backgroundColor);
    display: flex;
    font-size: 1.8em;
    line-height: 17px;
    padding: 5px;
    padding-left: 7px;

    @media screen and (max-width: $media-phone) {
      padding-left: 8px;
    }
  }

  .info-content {
    position: absolute;
    background-color: v-bind(backgroundColor);
    left: 30px;
    min-width: 100px;
    width: auto;
    border-radius: 7px;
    padding: 4px;
    border: 1px solid v-bind(mainColor);
    z-index: 50;

    .info-text {
      text-transform: none;
      font-size: 0.75em;
    }
  }
}

.input-checkbox {
  display: flex;
  gap: 10px;

  &-hide-file {
    color: v-bind(mainColor);
  }

  &-label {
    color: v-bind(mainColor);
  }
}
</style>
